/**
 * Error logging
 *
 * Can be used to log errors to console or and eternal
 * error logging system, like Sentry for example.
 *
 */

import config from '../config';
import { responseApiErrorInfo } from './errors';

// Create a placeholder for Sentry that will be loaded only when needed
let SentryPromise = null;

/**
 * Dynamically imports Sentry only when needed.
 *
 * Imports `./sentryClient` rather than `@sentry/react` directly: that module
 * re-exports only the handful of functions used below as static named
 * imports, which is what lets webpack tree-shake the rest of the SDK barrel
 * (Replay/rrweb, Feedback, Profiling, Tracing) out of this chunk. Adding a new
 * `Sentry.<x>` call below therefore requires adding `x` to sentryClient.js.
 *
 * @returns {Promise} Promise that resolves with the Sentry module
 */
const loadSentry = () => {
  if (!SentryPromise && config.sentryDsn) {
    // Dynamic import to create a separate chunk for Sentry
    SentryPromise = import(/* webpackChunkName: "sentry" */ './sentryClient');
  }
  return SentryPromise;
};

// Uncaught errors/rejections that happen before the (deferred) Sentry init are
// buffered here and captured right after init, so deferring Sentry out of the
// load window doesn't lose crashes from exactly that window.
const EARLY_ERROR_BUFFER_LIMIT = 20;
let earlyErrors = [];
let earlyListenersAttached = false;

const onEarlyError = (event) => {
  const error = event?.error || event?.reason;
  if (error && earlyErrors.length < EARLY_ERROR_BUFFER_LIMIT) {
    earlyErrors.push(error);
  }
};

const attachEarlyErrorListeners = () => {
  if (earlyListenersAttached || typeof window === 'undefined') return;
  earlyListenersAttached = true;
  window.addEventListener('error', onEarlyError);
  window.addEventListener('unhandledrejection', onEarlyError);
};

const detachEarlyErrorListeners = () => {
  if (!earlyListenersAttached) return;
  earlyListenersAttached = false;
  window.removeEventListener('error', onEarlyError);
  window.removeEventListener('unhandledrejection', onEarlyError);
};

// Runs `cb` once the browser is idle after the window `load` event — i.e.
// outside the LCP/INP-relevant load window that Core Web Vitals measure.
const runWhenIdleAfterLoad = (cb) => {
  const whenIdle = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(cb, { timeout: 10000 });
    } else {
      // Safari has no requestIdleCallback
      setTimeout(cb, 3000);
    }
  };
  if (document.readyState === 'complete') {
    whenIdle();
  } else {
    window.addEventListener('load', whenIdle, { once: true });
  }
};

// User identity requested before Sentry finished initializing.
// undefined = never set, null = explicitly cleared, object = user.
let pendingUser;

let sentryInitPromise = null;

/**
 * Loads AND initializes the Sentry client (idempotent). Called from the
 * deferred logSetup schedule, and directly from logError so that an error
 * occurring before the idle deadline still forces init and gets captured.
 *
 * @returns {Promise|null} Promise resolving with the initialized Sentry module
 */
const initSentry = () => {
  if (!config.sentryDsn) return null;
  if (!sentryInitPromise) {
    sentryInitPromise = loadSentry().then((Sentry) => {
      Sentry.init({
        dsn: config.sentryDsn,
        environment: config.env,
        integrations: [],
        release: currentVersion(),
        ignoreErrors: IGNORE_ERRORS,
        denyUrls: [
          // Google ReCAPTCHA scripts
          /recaptcha\/releases\/.*/i,
          // react-big-calendar internals (e.g. selectDates getDate on empty slots)
          /react-big-calendar/,
        ],
        beforeSend(event, hint) {
          // Translation proxies (Google Translate serves us from
          // *.translate.goog) rewrite the document and break same-origin
          // requests — e.g. POST /api/events/tracking answers 405, once per
          // tracked interaction (VANSITE-FRONTEND-4QB). Errors from such a
          // session describe the proxy, not our app.
          if (/(^|\.)translate\.goog$/i.test(window.location.hostname)) {
            return null;
          }
          // Headless crawlers (HeadlessChrome 68 from AWS us-west-2 scraping
          // profile pages, VANSITE-FRONTEND-4Q5/4Q6) run below our browserslist
          // floor and trip on missing polyfills — not user-facing.
          if (/HeadlessChrome/i.test(window.navigator?.userAgent || '')) {
            return null;
          }
          // Drop errors that originate from inline HTML/JSON-LD with no
          // first-party JS stack — these are scrapers, bots, and browser
          // extensions polluting Sentry, not user-facing bugs.
          const exception = hint?.originalException;
          const message = exception?.message || event?.message || '';
          const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
          const hasFirstPartyFrame = frames.some(
            (f) => f.in_app === true || /\/static\/js\//.test(f.filename || ''),
          );
          if (!hasFirstPartyFrame && /@context["'\]\s]*\.toLowerCase/.test(message)) {
            return null;
          }
          return event;
        },
      });

      if (pendingUser !== undefined) {
        Sentry.setUser(pendingUser);
      }

      // Capture crashes that happened before init, then hand off to Sentry's
      // own global handlers.
      detachEarlyErrorListeners();
      earlyErrors.forEach((error) => Sentry.captureException(error));
      earlyErrors = [];

      return Sentry;
    });
  }
  return sentryInitPromise;
};

const currentVersion = () => {
  if (window.currentAppVersion) {
    return `app@${window.currentAppVersion}`;
  } else if (process.env.REACT_APP_VERSION) {
    window.currentAppVersion = process.env.REACT_APP_VERSION;
    return `web@${process.env.REACT_APP_VERSION}`;
  }
  return 'unknown';
};


const IGNORE_ERRORS = [
            // === Browser/network connectivity (transient, environmental) ===
            'Load failed',
            'Failed to fetch',
            'NetworkError',
            'Network request failed',
            /network error/i,
            'ERR_INTERNET_DISCONNECTED',
            'ERR_NETWORK',
            'The Internet connection appears to be offline',
            'A server with the specified hostname could not be found',
            'The network connection was lost',
            'Software caused connection abort',
            'No network connection available',
            'Connection reset',
            'connection was lost',
            // German Safari translations of network errors
            'Die Netzwerkverbindung wurde unterbrochen',
            'Es wurde kein Server mit dem angegebenen Hostnamen gefunden',
            // Android okhttp connection drops (cordova-plugin-advanced-http)
            'unexpected end of stream',
            /com\.android\.okhttp/,
            'SSLException',
            'SocketTimeoutException',
            'SocketException',
            // Android/Cordova DNS errors
            'Host could not be resolved',
            'UnknownHostException',
            'No address associated with hostname',
            'Unable to resolve host',
            // Chromium net errors
            'ERR_NAME_NOT_RESOLVED',
            'ERR_CONNECTION_RESET',
            'ERR_CONNECTION_CLOSED',
            'ERR_CONNECTION_TIMED_OUT',
            'ERR_TIMED_OUT',
            'ERR_ADDRESS_UNREACHABLE',
            // SDK / fetch timeouts
            'timeout exceeded',
            'Request timeout',
            'The request timed out',
            // === User-initiated cancellations (not bugs) ===
            // User navigated away / clicked cancel / killed share sheet
            'AbortError',
            /Fetch is aborted/i,
            /Abort due to cancellation of share/,
            /User denied Geolocation/,
            // === Backend rate-limit (expected) ===
            /Too many requests from this IP/,
            // === Origin returned HTML instead of JSON (server-side, not client bug) ===
            /^<!DOCTYPE/i,
            /Unexpected token '<'/,
            // === Bot / scraper / browser-extension noise ===
            // Inline JSON-LD scrapers and Twitter/Reddit cards reading non-existent fields.
            // Permissive on the chars between `@context` and `.toLowerCase` so it matches
            // both `@context.toLowerCase` and bracket variants like `["@context"].toLowerCase`.
            /@context["'\]\s]*\.toLowerCase/,
            // Generic "[object Object]" stringified by an extension treating it as JSON.
            // Quotes around the literal aren't always present, so don't require them.
            /\[object Object\][^\n]*is not valid JSON/,
            // Stack overflow nearly always originates from a browser extension or scraper
            'Maximum call stack size exceeded',
            // Google ReCAPTCHA internal errors (third-party, not actionable)
            /Cannot read properties of undefined \(reading 'hpm'\)/,
            // Facebook in-app browser injects scripts that call iOS native bridge
            /window\.webkit\.messageHandlers/,
            // === Stripe / payment integration races (expected) ===
            /You cannot update Payment Request options while the payment sheet is showing/,
            // Customer revisits /checkout after the PaymentIntent already
            // succeeded; Stripe.js then refuses to update payment_method_types
            // on a terminal PI (VANSITE-FRONTEND-4HQ).
            /payment_method_types could not be updated/,
            /payment_intent_unexpected_state/,
            // === Auth race conditions (expected when user double-taps) ===
            'Login or logout already in progress',
            // === User-input validation errors that throw (libphonenumber etc.) ===
            'INVALID_COUNTRY',
            // === Browser-native page translation vs. React's DOM bookkeeping ===
            // Chrome/Safari replace text nodes in place while translating, so
            // React later removes or re-inserts a node the browser already
            // swapped out. Not fixable from our side short of suppressing
            // translation (VANSITE-FRONTEND-4MY).
            /Failed to execute 'removeChild' on 'Node'/,
            /Failed to execute 'insertBefore' on 'Node'/,
            // === Cordova plugin lifecycle (plugin not always loaded) ===
            // Any plugin can be absent on a given build/platform — cordova's own
            // module loader throws this before our code can feature-detect.
            // Covers cordova-plugin-inappbrowser and ionic-plugin-deeplinks
            // (VANSITE-FRONTEND-47Z).
            /Module .* does not exist/,
            // WebView teardown: the native side is gone while JS still holds a
            // bridge reference (backgrounded/killed app). Nothing to fix on the
            // JS side (VANSITE-FRONTEND-4J9 / -4PJ).
            /Error invoking postMessage/,
];

/**
 * Set up error handling. If a Sentry DSN is provided a Sentry client will be
 * installed — but only once the browser is idle after `load`: downloading,
 * parsing and initializing Sentry must not compete with hydration for the
 * main thread during the load window that Core Web Vitals measure. Uncaught
 * errors before init are buffered and captured right after it; logError()
 * forces an immediate init.
 */
export const logSetup = () => {
  if (!config.sentryDsn || typeof window === 'undefined') return;
  attachEarlyErrorListeners();
  runWhenIdleAfterLoad(() => {
    const init = initSentry();
    if (init) {
      init.catch((e) => {
        console.error('Failed to load Sentry:', e);
      });
    }
  });
};

/**
 * Set user ID for the logger so that it
 * can be attached to Sentry issues.
 *
 * @param {string} userId ID of current user
 */
export const logSetUserId = (userId) => {
  if (config.sentryDsn) {
    // Don't force the (deferred) Sentry init just to attach the user id:
    // remember it and let initSentry apply it, or set it right away when the
    // client is already up.
    pendingUser = { id: userId };
    if (sentryInitPromise) {
      sentryInitPromise
        .then((Sentry) => {
          Sentry.setUser(pendingUser);
        })
        .catch((e) => {
          console.error('Failed to set user ID in Sentry:', e);
        });
    }
  }
};

/**
 * Clears the user ID.
 */
export const logClearUserId = () => {
  if (config.sentryDsn) {
    pendingUser = null;
    if (sentryInitPromise) {
      sentryInitPromise
        .then((Sentry) => {
          Sentry.setUser(null);
        })
        .catch((e) => {
          console.error('Failed to clear user ID in Sentry:', e);
        });
    }
  }
};

const printAPIErrorsAsConsoleTable = (apiErrors) => {
  /* Logging needs some console output */
  if (apiErrors != null && apiErrors.length > 0 && typeof console.table === 'function') {
    console.log('Errors returned by Marketplace API call:');
    console.table(apiErrors.map((err) => ({ status: err.status, code: err.code, ...err.meta })));
  }
};

// Non-actionable error signatures that should not be reported to Sentry.
// User-input validation, rate-limit hits, and conflict responses are expected
// runtime outcomes, not bugs worth tracking.
const NON_ACTIONABLE_API_CODES = new Set([
  'validation-invalid-params',
  'validation-invalid-value',
  'email-not-found',
  'email-taken',
  'expired-verification-code',
  'invalid-verification-code',
]);

const isSharetribeRequest = (e) =>
  e?.name === 'SharetribeApiError' ||
  /sharetribe/i.test(e?.config?.baseURL || '') ||
  /sharetribe/i.test(e?.config?.url || '');

export const isNonActionableError = (e, apiErrors) => {
  const status = e?.status;
  // Rate-limited and conflict are user/session conditions, not bugs.
  if (status === 429 || status === 409) return true;
  // Gateway/proxy timeouts and unavailability (502/503/504) are transient infra
  // conditions, not application bugs — the upstream was slow or briefly down.
  // The UI already degrades to a retry prompt; surfacing these (previously as a
  // misleading SyntaxError) only flooded Sentry (VANSITE-FRONTEND-49X).
  if (status === 502 || status === 503 || status === 504) return true;
  // 401 is only non-actionable when it comes from the Sharetribe SDK
  // (expired/refresh-failed session). Other 401s — from our own backend
  // or third parties — may indicate real auth bugs and should still be reported.
  if (status === 401 && isSharetribeRequest(e)) return true;
  // Same for Sharetribe 403 forbidden: long-lived mobile WebView sessions go
  // stale (or the user was banned) and every fetch-current-user then 403s.
  // A session condition, not a bug (Sentry VANSITE-FRONTEND-4A9/4AX/4B0).
  if (status === 403 && isSharetribeRequest(e)) return true;
  // Sharetribe 404 not-found: the requested listing/user/transaction was
  // deleted or the link is stale. The UI already routes these to a not-found
  // page; a missing resource is not a bug worth tracking
  // (Sentry VANSITE-FRONTEND-4BE show-listing-failed).
  if (status === 404 && isSharetribeRequest(e)) return true;
  // Status 400 with a known user-input validation code from the SDK / API
  if (
    status === 400 &&
    apiErrors?.some((err) => NON_ACTIONABLE_API_CODES.has(err.code))
  ) {
    return true;
  }
  // Any apiError carrying a non-actionable code, regardless of overall status
  if (apiErrors?.some((err) => NON_ACTIONABLE_API_CODES.has(err?.code))) {
    return true;
  }
  // Cordova HTTP plugin uses negative status codes for transport-layer
  // failures (DNS, SSL, timeout, abort) — environmental, not bugs.
  if (typeof status === 'number' && status < 0) return true;
  // A bare DOM Event as the error payload (serialises to `{ isTrusted: true }`)
  // is what a failed resource/XHR load hands its error handler. It carries no
  // message, no status and no stack, so it groups into one undiagnosable issue
  // per call site (VANSITE-FRONTEND-4MQ) — environmental, not a bug.
  if (typeof Event !== 'undefined' && e instanceof Event) return true;
  // Origin returned an HTML page instead of JSON (CDN error page, not a client bug)
  const message = typeof e?.message === 'string' ? e.message : '';
  if (message.startsWith('<!DOCTYPE') || message.startsWith('<html')) return true;
  return false;
};

/**
 * Grouping fingerprint for non-Error payloads that logError wraps in a
 * synthetic Error. Their stack always points at the wrapper in this file, so
 * Sentry's stack-based grouping folded unrelated failures into a single issue
 * (VANSITE-FRONTEND-47M) — group them by their logical error code and HTTP
 * status instead.
 *
 * @param {string} code Error code passed to logError
 * @param {number} [status] HTTP status of the wrapped payload, if any
 * @returns {string[]} Sentry fingerprint
 */
export const syntheticErrorFingerprint = (code, status, apiErrorCode) => {
  const fingerprint = [code];
  if (status != null) {
    fingerprint.push(String(status));
  }
  // code + status alone is too coarse for the API wrappers: every failing
  // `post-cordova-error` 400 collapsed into a single Sentry issue regardless of
  // cause, mixing `validation-invalid-value` (negative payin on
  // /api/initiate-privileged), `validation-invalid-params` and plain
  // `bad-request` under one group with one misleading user count
  // (VANSITE-FRONTEND-4MG). The Sharetribe error code separates them.
  if (apiErrorCode) {
    fingerprint.push(String(apiErrorCode));
  }
  return fingerprint;
};

/**
 * Logs an exception. If Sentry is configured
 * sends the error information there. Otherwise
 * prints the error to the console.
 *
 * @param {Error} e Error that occurred
 * @param {string} code Error code
 * @param {object} data Additional data to be sent to Sentry
 */
export const logError = (e, code, data = {}) => {
  const apiErrors = responseApiErrorInfo(e);
  if (e?.status !== 404) {
    if (e instanceof Error) {
      console.error(code, e.message, e.stack, data);
    } else {
      let serialized;
      try {
        serialized = JSON.stringify(e, null, 2);
      } catch {
        // e may contain cyclic references — fall back to a plain string.
        serialized = String(e);
      }
      console.error(code, serialized, data);
    }
  }
  if (isNonActionableError(e, apiErrors)) {
    return;
  }
  if (config.sentryDsn) {
    // Force init (idempotent): an error before the deferred idle init must
    // still reach Sentry.
    initSentry()
      .then((Sentry) => {
        const extra = { ...data, apiErrorData: apiErrors };

        // If the error is not a proper Error instance (e.g. SDK response object),
        // wrap it so Sentry captures a stack trace and groups it properly.
        let error = e;
        let fingerprint = null;
        if (!(e instanceof Error)) {
          const message = e?.message || e?.detail || e?.error || e?.statusText || code;
          const status = e?.status;
          error = new Error(`[${code}] ${status ? `${status} - ` : ''}${message}`);
          fingerprint = syntheticErrorFingerprint(code, status, apiErrors[0]?.code);
          try {
            extra.originalError = JSON.parse(JSON.stringify(e || {}));
          } catch {
            extra.originalError = String(e);
          }
        }

        Sentry.withScope((scope) => {
          scope.setTag('code', code);
          if (fingerprint) {
            scope.setFingerprint(fingerprint);
          }
          Object.keys(extra).forEach((key) => {
            scope.setExtra(key, extra[key]);
          });
          Sentry.captureException(error);
        });
      })
      .catch((err) => {
        console.error('Failed to log error to Sentry:', err);
        // Still print API errors even if Sentry fails
        printAPIErrorsAsConsoleTable(apiErrors);
      });
  }
};
